import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { AppContext } from '../../context/AppContext';
import { EventoComunitario, Socio, Carrera } from '../../models/types';
import { FaCalendarPlus, FaCalendarAlt, FaUsers, FaMapMarkerAlt, FaClock, FaTrophy } from 'react-icons/fa';

// Breakpoints
const breakpoints = {
  mobile: '768px',
  tablet: '1024px'
};

// Estilos
const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 8px;
    margin: 0;
    max-width: 100%;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  
  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
    gap: 16px;
    margin-bottom: 16px;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #101325;
  margin: 0;
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.5rem;
    text-align: center;
  }
`;

const Button = styled.button`
  background-color: #101325;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 1rem;
  
  svg {
    margin-right: 8px;
  }
  
  &:hover {
    background-color: #1a1f3d;
  }
`;

const CalendarNav = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const MonthDisplay = styled.h2`
  font-size: 1.5rem;
  color: #101325;
  margin: 0;
`;

const NavButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #101325;
  
  &:hover {
    color: #e4312e;
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 10px;
  
  @media (max-width: ${breakpoints.mobile}) {
    gap: 2px;
    background: #f5f5f5;
    border-radius: 8px;
    padding: 4px;
  }
`;

const DayHeader = styled.div`
  text-align: center;
  font-weight: bold;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 5px;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 8px 4px;
    font-size: 0.8rem;
    background-color: #e9ecef;
    border-radius: 4px;
  }
`;

const DayCell = styled.div<{ $isCurrentMonth: boolean; $isToday: boolean; $hasEvents?: boolean }>`
  min-height: 120px;
  border-radius: 5px;
  background-color: ${props => props.$isToday ? '#f8f9fa' : 'white'};
  border: 1px solid ${props => props.$isToday ? '#101325' : '#ddd'};
  opacity: ${props => props.$isCurrentMonth ? 1 : 0.5};
  padding: 5px;
  position: relative;
  cursor: ${props => props.$hasEvents ? 'pointer' : 'default'};
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${props => props.$hasEvents ? '#f0f4f8' : (props.$isToday ? '#f8f9fa' : 'white')};
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    min-height: 45px;
    height: 45px;
    padding: 2px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    aspect-ratio: 1;
    border-radius: 4px;
    
    &:hover {
      background-color: ${props => props.$hasEvents ? '#e8f2ff' : (props.$isToday ? '#f8f9fa' : 'white')};
    }
  }
`;

const DayNumber = styled.div`
  font-weight: bold;
  text-align: right;
  padding: 5px;
  
  @media (max-width: ${breakpoints.mobile}) {
    text-align: center;
    padding: 0;
    font-size: 0.9rem;
    line-height: 1;
  }
`;

const EventsList = styled.div`
  margin-top: 5px;
  
  @media (max-width: ${breakpoints.mobile}) {
    display: none;
  }
`;

const EventItem = styled.div<{ $tipo: 'carrera_especial' | 'entrenamiento' | 'evento_social' | 'clinica' | 'competicion_amistosa' }>`
  background-color: ${props => {
    switch (props.$tipo) {
      case 'carrera_especial': return '#e4312e';
      case 'entrenamiento': return '#4caf50';
      case 'evento_social': return '#2196f3';
      case 'clinica': return '#9c27b0';
      case 'competicion_amistosa': return '#ff9800';
      default: return '#101325';
    }
  }};
  color: white;
  padding: 5px;
  border-radius: 3px;
  margin-bottom: 5px;
  font-size: 0.8rem;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  &:hover {
    filter: brightness(1.1);
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 2px 4px;
    font-size: 0.7rem;
    margin-bottom: 2px;
  }
`;

const ViewSelector = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
`;

const ViewButton = styled.button<{ $active: boolean }>`
  background-color: ${props => props.$active ? '#101325' : '#f0f0f0'};
  color: ${props => props.$active ? 'white' : '#333'};
  border: none;
  padding: 8px 15px;
  border-radius: 5px;
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.$active ? '#1a1f3d' : '#e0e0e0'};
  }
`;

const ListViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const EventCard = styled.div`
  background-color: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  
  @media (min-width: 768px) {
    flex-direction: row;
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 12px;
    border-radius: 8px;
  }
`;

const EventDate = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 5px;
  margin-right: 20px;
  margin-bottom: 15px;
  
  @media (min-width: 768px) {
    margin-bottom: 0;
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 10px;
    margin-right: 0;
    margin-bottom: 10px;
    min-width: 80px;
  }
`;

const EventDay = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: #101325;
`;

const EventMonth = styled.div`
  font-size: 1rem;
  color: #666;
`;

const EventDetails = styled.div`
  flex: 1;
`;

const EventIndicator = styled.div`
  display: none;
  
  @media (max-width: ${breakpoints.mobile}) {
    display: flex;
    gap: 2px;
    justify-content: center;
    align-items: center;
    height: 8px;
  }
`;

const EventDot = styled.div<{ $tipo: 'carrera_especial' | 'entrenamiento' | 'evento_social' | 'clinica' | 'competicion_amistosa' }>`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: ${props => {
    switch (props.$tipo) {
      case 'carrera_especial': return '#e4312e';
      case 'entrenamiento': return '#4caf50';
      case 'evento_social': return '#2196f3';
      case 'clinica': return '#9c27b0';
      case 'competicion_amistosa': return '#ff9800';
      default: return '#101325';
    }
  }};
`;

const MobileEventsList = styled.div`
  display: none;
  
  @media (max-width: ${breakpoints.mobile}) {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 16px;
    padding: 16px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
`;

const MobileEventItem = styled.div<{ $tipo: 'carrera_especial' | 'entrenamiento' | 'evento_social' | 'clinica' | 'competicion_amistosa' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 6px;
  background-color: ${props => {
    switch (props.$tipo) {
      case 'carrera_especial': return 'rgba(228, 49, 46, 0.1)';
      case 'entrenamiento': return 'rgba(76, 175, 80, 0.1)';
      case 'evento_social': return 'rgba(33, 150, 243, 0.1)';
      case 'clinica': return 'rgba(156, 39, 176, 0.1)';
      case 'competicion_amistosa': return 'rgba(255, 152, 0, 0.1)';
      default: return 'rgba(16, 19, 37, 0.1)';
    }
  }};
  border-left: 3px solid ${props => {
    switch (props.$tipo) {
      case 'carrera_especial': return '#e4312e';
      case 'entrenamiento': return '#4caf50';
      case 'evento_social': return '#2196f3';
      case 'clinica': return '#9c27b0';
      case 'competicion_amistosa': return '#ff9800';
      default: return '#101325';
    }
  }};
  
  h4 {
    margin: 0;
    font-size: 0.9rem;
    color: #333;
  }
  
     p {
     margin: 0;
     font-size: 0.8rem;
     color: #666;
   }
 `;

const EventTitle = styled.h3`
  font-size: 1.3rem;
  margin: 0 0 10px 0;
  color: #101325;
`;

const EventInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 15px;
`;

const EventInfoItem = styled.div`
  display: flex;
  align-items: center;
  color: #666;
  
  svg {
    margin-right: 5px;
  }
`;

const EventActions = styled.div`
  display: flex;
  gap: 10px;
`;

const EventDescription = styled.p`
  margin: 0 0 15px 0;
  color: #333;
`;

const EventTag = styled.span<{ $tipo: 'carrera_especial' | 'entrenamiento' | 'evento_social' | 'clinica' | 'competicion_amistosa' }>`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 20px;
  font-size: 0.8rem;
  background-color: ${props => {
    switch (props.$tipo) {
      case 'carrera_especial': return '#e4312e';
      case 'entrenamiento': return '#4caf50';
      case 'evento_social': return '#2196f3';
      case 'clinica': return '#9c27b0';
      case 'competicion_amistosa': return '#ff9800';
      default: return '#101325';
    }
  }};
  color: white;
  margin-right: 10px;
`;

const EventosCalendar: React.FC = () => {
  const navigate = useNavigate();
  const { data } = useContext(AppContext);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [eventos, setEventos] = useState<EventoComunitario[]>([]);
  const [carrerasFormateadas, setCarrerasFormateadas] = useState<EventoComunitario[]>([]);
  
  // Debounce para evitar múltiples re-renders rápidos que pueden causar ResizeObserver loops
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Convertir carreras a formato de eventos para mostrarlas en el calendario
  useEffect(() => {
    if (data.carreras && data.carreras.length > 0) {
      const carrerasComoEventos: EventoComunitario[] = data.carreras.map((carrera: Carrera) => ({
        id: `carrera-${carrera.id}`,
        nombre: `${carrera.nombre} (Carrera Oficial)`,
        descripcion: `Carrera oficial de la liga ${carrera.ligaId}`,
        fecha: carrera.fecha,
        horaInicio: '20:00', // Hora por defecto para las carreras
        duracion: 120,
        tipo: 'carrera_especial',
        circuito: carrera.circuito,
        organizador: 'admin',
        participantesRegistrados: [],
        capacidadMaxima: 30,
        estado: 'programado',
        esCarreraOficial: true
      }));
      
      setCarrerasFormateadas(carrerasComoEventos);
    }
  }, [data.carreras]);

  // Inicializar eventos comunitarios
  useEffect(() => {
    if (data.eventosComunitarios && data.eventosComunitarios.length > 0) {
      setEventos(data.eventosComunitarios);
    } else {
      setEventos([]); // No cargar eventos de ejemplo, solo reales
    }
  }, [data.eventosComunitarios]);
  
  // Funciones para navegar por el calendario
  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };
  
  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };
  
  const goToToday = () => {
    setCurrentDate(new Date());
  };
  
  // Obtener días para el calendario (comenzando en lunes)
  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // Primer día del mes
    const firstDay = new Date(year, month, 1);
    // Último día del mes
    const lastDay = new Date(year, month + 1, 0);
    
    // Día de la semana del primer día (0=Domingo, 1=Lunes, ..., 6=Sábado)
    let firstDayOfWeek = firstDay.getDay();
    // Ajustar para que la semana comience en lunes (0=Lunes, 1=Martes, ..., 6=Domingo)
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    
    // Días del mes anterior para completar la primera semana
    const daysFromPrevMonth = firstDayOfWeek;
    
    // Días del mes siguiente para completar la última semana
    const lastDayOfWeek = lastDay.getDay();
    const daysFromNextMonth = lastDayOfWeek === 0 ? 0 : 7 - lastDayOfWeek;
    
    // Crear array con todos los días a mostrar
    const days = [];
    
    // Días del mes anterior
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false
      });
    }
    
    // Días del mes actual
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Días del mes siguiente
    for (let i = 1; i <= daysFromNextMonth; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    
    return days;
  };
  
  // Función para normalizar fechas sin tener en cuenta la zona horaria
  const normalizeDate = (date: Date) => {
    const d = new Date(date);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  // Obtener eventos para un día específico
  const getEventsForDay = (date: Date) => {
    // Normalizar la fecha objetivo (sin hora)
    const targetDate = normalizeDate(date);
    
    // Filtrar eventos que coincidan con la fecha
    const eventosDelDia = eventos.filter(evento => {
      const eventDate = normalizeDate(new Date(evento.fecha));
      return eventDate.getTime() === targetDate.getTime();
    });
    
    // Filtrar carreras que coincidan con la fecha
    const carrerasDelDia = carrerasFormateadas.filter(carrera => {
      const eventDate = normalizeDate(new Date(carrera.fecha));
      return eventDate.getTime() === targetDate.getTime();
    });
    
    // Combinar ambos arrays
    return [...eventosDelDia, ...carrerasDelDia];
  };
  
  // Obtener todos los eventos para la vista de lista
  const getEventsForList = () => {
    // Filtrar eventos futuros y ordenar por fecha
    const today = new Date().toISOString().split('T')[0];
    const todosLosEventos = [...eventos, ...carrerasFormateadas];
    
    return todosLosEventos
      .filter(evento => evento.fecha >= today)
      .sort((a, b) => a.fecha.localeCompare(b.fecha));
  };
  
  // Formatear fecha para mostrar
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return date.toLocaleDateString('es-ES', options);
  };
  
  // Obtener nombre del organizador
  const getNombreOrganizador = (organizadorId: string) => {
    const organizador = data.socios.find(s => s.id === organizadorId);
    return organizador ? `${organizador.nombre} ${organizador.apellido}` : 'Organizador desconocido';
  };
  
  // Renderizar calendario
  const renderCalendar = () => {
    const days = getDaysInMonth();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Días de la semana comenzando por lunes
    const dayNames = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
    
    return (
      <>
        <CalendarNav>
          <NavButton onClick={prevMonth}>&lt;</NavButton>
          <MonthDisplay>
            {currentDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
          </MonthDisplay>
          <div>
            <NavButton onClick={goToToday}>Hoy</NavButton>
            <NavButton onClick={nextMonth}>&gt;</NavButton>
          </div>
        </CalendarNav>
        
        <CalendarGrid>
          {dayNames.map(day => (
            <DayHeader key={day}>{day}</DayHeader>
          ))}
          
          {days.map((day, index) => {
            const isToday = day.date.getTime() === today.getTime();
            const eventosForDay = getEventsForDay(day.date);
            const hasEvents = eventosForDay.length > 0;
            
            return (
              <DayCell 
                key={index} 
                $isCurrentMonth={day.isCurrentMonth}
                $isToday={isToday}
                $hasEvents={hasEvents}
                onClick={() => {
                  if (hasEvents && !isTransitioning) {
                    setIsTransitioning(true);
                    requestAnimationFrame(() => {
                      setSelectedDate(day.date);
                      setTimeout(() => setIsTransitioning(false), 100);
                    });
                  }
                }}
              >
                <DayNumber>{day.date.getDate()}</DayNumber>
                
                {/* Vista desktop - mostrar eventos como antes */}
                <EventsList>
                  {eventosForDay.map(evento => (
                    <EventItem 
                      key={evento.id}
                      $tipo={evento.tipo}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/eventos/${evento.id}`);
                      }}
                    >
                      {evento.nombre}
                    </EventItem>
                  ))}
                </EventsList>
                
                {/* Vista móvil - mostrar solo indicadores */}
                {hasEvents && (
                  <EventIndicator>
                    {eventosForDay.slice(0, 3).map((evento, idx) => (
                      <EventDot key={idx} $tipo={evento.tipo} />
                    ))}
                    {eventosForDay.length > 3 && (
                      <span style={{ fontSize: '6px', color: '#666', marginLeft: '2px' }}>+{eventosForDay.length - 3}</span>
                    )}
                  </EventIndicator>
                )}
              </DayCell>
            );
          })}
        </CalendarGrid>
        
        {/* Lista de eventos del día seleccionado en móvil */}
        {selectedDate && (
          <MobileEventsList>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#333' }}>
                {selectedDate.toLocaleDateString('es-ES', { 
                  weekday: 'long', 
                  day: 'numeric', 
                  month: 'long' 
                })}
              </h3>
              <button 
                onClick={() => {
                  if (!isTransitioning) {
                    setIsTransitioning(true);
                    requestAnimationFrame(() => {
                      setSelectedDate(null);
                      setTimeout(() => setIsTransitioning(false), 100);
                    });
                  }
                }}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '1.2rem', 
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ✕
              </button>
            </div>
            {getEventsForDay(selectedDate).map(evento => (
              <MobileEventItem 
                key={evento.id} 
                $tipo={evento.tipo}
                onClick={() => {
                  navigate(`/eventos/${evento.id}`);
                  setSelectedDate(null);
                }}
              >
                <div>
                  <h4>{evento.nombre}</h4>
                  <p>{evento.horaInicio} • {evento.descripcion}</p>
                </div>
              </MobileEventItem>
            ))}
          </MobileEventsList>
        )}
      </>
    );
  };
  
  // Renderizar lista de eventos
  const renderEventsList = () => {
    const eventosFuturos = getEventsForList();
    
    return (
      <ListViewContainer>
        {eventosFuturos.length > 0 ? (
          eventosFuturos.map(evento => {
            const date = new Date(evento.fecha);
            const day = date.getDate();
            const month = date.toLocaleDateString('es-ES', { month: 'short' });
            
            return (
              <EventCard key={evento.id}>
                <EventDate>
                  <EventDay>{day}</EventDay>
                  <EventMonth>{month}</EventMonth>
                </EventDate>
                
                <EventDetails>
                  <EventTitle>
                    <EventTag $tipo={evento.tipo}>
                      {evento.tipo === 'carrera_especial' && 'Carrera'}
                      {evento.tipo === 'entrenamiento' && 'Entrenamiento'}
                      {evento.tipo === 'evento_social' && 'Social'}
                      {evento.tipo === 'clinica' && 'Clínica'}
                      {evento.tipo === 'competicion_amistosa' && 'Competición'}
                    </EventTag>
                    {evento.nombre}
                  </EventTitle>
                  
                  <EventInfo>
                    <EventInfoItem>
                      <FaCalendarAlt /> {formatDate(evento.fecha)}
                    </EventInfoItem>
                    <EventInfoItem>
                      <FaClock /> {evento.horaInicio}
                    </EventInfoItem>
                    {evento.circuito && (
                      <EventInfoItem>
                        <FaMapMarkerAlt /> {evento.circuito}
                      </EventInfoItem>
                    )}
                    <EventInfoItem>
                      <FaTrophy /> Organizador: {getNombreOrganizador(evento.organizador)}
                    </EventInfoItem>
                  </EventInfo>
                  
                  <EventDescription>{evento.descripcion}</EventDescription>
                  
                  <EventActions>
                    <Button onClick={() => navigate(`/eventos/${evento.id}`)}>
                      Ver detalles
                    </Button>
                  </EventActions>
                </EventDetails>
              </EventCard>
            );
          })
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <h3>No hay eventos próximos</h3>
            <p>¡Sé el primero en organizar un evento para la comunidad!</p>
          </div>
        )}
      </ListViewContainer>
    );
  };
  
  return (
    <Container>
      <Header>
        <Title>Calendario de Eventos</Title>
        <Button onClick={() => navigate('/eventos/new')}>
          <FaCalendarPlus /> Crear Evento
        </Button>
      </Header>
      
      <ViewSelector>
        <ViewButton 
          $active={viewMode === 'calendar'}
          onClick={() => setViewMode('calendar')}
        >
          Vista de Calendario
        </ViewButton>
        <ViewButton 
          $active={viewMode === 'list'}
          onClick={() => setViewMode('list')}
        >
          Vista de Lista
        </ViewButton>
      </ViewSelector>
      
      {viewMode === 'calendar' ? renderCalendar() : renderEventsList()}
    </Container>
  );
};

export default EventosCalendar;
